import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { UNSAFE_NavigationContext } from 'react-router-dom';

// Asks before leaving a page whose edits haven't been saved.
//
// The app mounts a plain <BrowserRouter>, where React Router's useBlocker isn't
// available (it needs a data router), so in-app navigation — tab NavLinks,
// <Link>s and navigate() calls, navigate(-1) included — is caught by wrapping
// the router's history methods while the page is mounted. Reloading or closing
// the browser tab goes through beforeunload instead. The browser's own Back
// button isn't intercepted.
//
// `hasUnsavedChanges` is called at the moment someone tries to leave, so it can
// read refs rather than state the page would have to re-render for. Render
// <ConfirmModal isOpen={isPrompting} onConfirm={confirmLeave}
// onCancel={cancelLeave} /> to ask.
export default function useUnsavedChangesGuard(hasUnsavedChanges) {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);
  hasUnsavedChangesRef.current = hasUnsavedChanges;
  // Set once the user has said to leave anyway, so the navigation they chose
  // isn't asked about again — navigate(-1) back to a page that was a full load
  // would otherwise also raise the browser's own beforeunload dialog.
  const leavingRef = useRef(false);
  const [pendingLeave, setPendingLeave] = useState(null);

  useEffect(() => {
    if (!navigator) return undefined;
    const original = { push: navigator.push, replace: navigator.replace, go: navigator.go };
    const guarded = {};
    for (const method of Object.keys(original)) {
      guarded[method] = (...args) => {
        const leave = () => original[method].apply(navigator, args);
        if (leavingRef.current || !hasUnsavedChangesRef.current()) return leave();
        setPendingLeave(() => leave);
      };
      navigator[method] = guarded[method];
    }
    return () => {
      for (const method of Object.keys(original)) {
        if (navigator[method] === guarded[method]) navigator[method] = original[method];
      }
    };
  }, [navigator]);

  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (leavingRef.current || !hasUnsavedChangesRef.current()) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  const confirmLeave = useCallback(() => {
    setPendingLeave(null);
    leavingRef.current = true;
    // A Back with nothing to go back to leaves the page where it was; guard
    // it again rather than staying switched off.
    setTimeout(() => {
      leavingRef.current = false;
    }, 1000);
    pendingLeave?.();
  }, [pendingLeave]);

  const cancelLeave = useCallback(() => setPendingLeave(null), []);

  return { isPrompting: Boolean(pendingLeave), confirmLeave, cancelLeave };
}
